/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2014, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * JQuery datatables grid control (JQGrid).
 * @module com/ibm/ui/render/grid/control/JQGrid
 */
define([
	'./GridControlUtil'
], (
	GridControlUtil
) => {

	/**
	 * Public class encapsulating the actual grid implementation that is able to support rendering the grid on the canvas and for pdf printing
	 */
	return class JQGrid {

		constructor(...args) {
			this.init(args[0]);
		}

		init(options = {}) {
			const GridClass = GridControlUtil.getGridClass(options.dashboardApi, options.content);
			this.grid = new GridClass(options);
		}

		/**
		 * Resets the table - should be called before the first call to setData.
		 * Calls by GridView
		 */
		reset() {
			this.grid.reset();
		}

		/**
		 * Calls by GridView
		 */
		setData(queryResult) {
			this.grid.setData(queryResult);
		}

		/**
		 * Calls by GridView
		 */
		hasData() {
			return this.grid.hasData();
		}

		/**
		 * Calls by GridView
		 */
		setStyles(styleObj) {
			this.grid.setStyles(styleObj);
		}

		/**
		 * Only call by unit tests
		 */
		resetOnRender() {
			this.grid.resetOnRender();
		}

		/**
		 * Only call by unit tests
		 */
		onResize() {
			this.grid.onResize();
		}

		render(resizing, renderingNewData) {
			return this.grid.render(resizing, renderingNewData);
		}

		/**
		 * Only call by unit tests
		 */
		onSyncScrollBar() {
			return this.grid.onSyncScrollBar();
		}

		/**
		 * Set selections context which contains row edge selection, column edge selection
		 * and datapoint selection
		 **/
		setSelections(selections) {
			this.grid.setSelections(selections);
		}

		// Not sure where this get use, most likel from no longer use ./Grid.js
		setValue($td, value, rowIndex, colIndex) {
			this.grid.setValue($td, value, rowIndex, colIndex);
		}

		/**
		 * Gets a list of the nodes that have been selected.
		 * Calls by GridEventTarget.js
		 * @return {Array.node} - an array of nodes
		 */
		getSelectedColumnHeaderNodes() {
			return this.grid.getSelectedColumnHeaderNodes();
		}

		/**
		 * @returns true if the current node includes the 'selected' or 'selected_secondary' or 'implied_selection' class.
		 */
		isCellSelected(node) {
			return this.grid.isCellSelected(node);
		}

		/**
		 * Calls by GridEventTarget.js
		 *
		 * @param {number} columnNodeOrColumnIndex column or row index to verify
		 * @return {boolean} true if the column/row is grouped
		 */
		isGroupedColumn(columnNodeOrColumnIndex) {
			return this.grid.isGroupedColumn(columnNodeOrColumnIndex);
		}

		/**
		 * Find the slot, index of slot in JQGrid._slots and item's mapIndex in that slot
		 * Calls by GridEventTarget.js
		 * @param colIndex Column index in the table
		 */
		findSlotDetails(colIndex) {
			return this.grid.findSlotDetails(colIndex);
		}

		/**
		 * Calls by GridEventTarget.js
		 */
		getRankDataItemCount() {
			return this.grid.getRankDataItemCount();
		}

		/**
		 * Determines whether the given column mapping is a rank column
		 * Calls by GridEventTarget.js
		 * @param columIndex:	index of the column in the table
		 */
		isRank(columIndex) {
			return this.grid.isRank(columIndex);
		}

		remove() {
			this.grid.remove();
		}

		/**
		 * Calls by GridView
		 */
		removeGridView() {
			this.grid.removeGridView();
		}

		/**
		 * Calls by GridView
		 */
		onEnterContainer() {
			this.grid.onEnterContainer();
		}

		/**
		 * Calls by GridEventTarget.js
		 */
		handleSelectColumnHeader(selectedNode, bSingleSelect, bRightClick) {
			this.grid.handleSelectColumnHeader(selectedNode, bSingleSelect, bRightClick);
		}

		showDropZone(selector) {
			this.grid.showDropZone(selector);
		}

		hideDropZones() {
			this.grid.hideDropZones();
		}

		showReplaceDropZone(idx, type='col') {
			this.grid.showReplaceDropZone(idx, type);
		}

		showLeftDropZone(col) {
			this.grid.showLeftDropZone(col);
		}

		showRightDropZone(col) {
			this.grid.showRightDropZone(col);
		}

		getDataRows () {
			return this.grid.getDataRows();
		}

		canResizeToWidget () {
			return this.grid.canResizeToWidget();
		}
	};
});
